import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiBell, FiMenu, FiLogOut, FiUser, FiSettings, FiMoon, FiSun, FiHeart } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import NotificationPanel from '../common/NotificationPanel';
import apiService from '../../services/api';
import logo from '../../assets/logo.png';
import logger from '../../utils/logger';

const HeaderContainer = styled.header`
  background-color: var(--background-primary);
  border-bottom: 1px solid var(--border-color);
  padding: 0 1.5rem;
  height: 64px;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: var(--shadow-sm);
  
  @media (max-width: 768px) {
    padding: 0 1rem;
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    grid-template-columns: none;
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  justify-self: start;
  
  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

const CenterSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileDonateSection = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    justify-content: center;
    width: 100%;
    padding: 0.75rem 1rem;
    background-color: var(--background-primary);
    border-bottom: 1px solid var(--border-color);
    box-shadow: var(--shadow-sm);
  }
`;

const DonateLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, var(--success-color) 0%, var(--success-dark) 100%);
  color: var(--text-inverse);
  text-decoration: none;
  border-radius: 0;
  font-size: 0.875rem;
  font-weight: 500;
  font-family: 'Inter', sans-serif;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(56, 161, 105, 0.2);
  
  &:hover {
    background: linear-gradient(135deg, var(--success-dark) 0%, var(--success-color) 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(56, 161, 105, 0.3);
    text-decoration: none;
    color: var(--text-inverse);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 768px) {
    display: none; /* Hide on mobile in LeftSection */
  }
`;

const MobileDonateLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 0.75rem;
  background: linear-gradient(135deg, var(--success-color) 0%, var(--success-dark) 100%);
  color: var(--text-inverse);
  text-decoration: none;
  border-radius: 0;
  font-size: 0.875rem;
  font-weight: 500;
  font-family: 'Inter', sans-serif;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(56, 161, 105, 0.2);
  width: auto;
  white-space: nowrap;
  
  &:hover {
    background: linear-gradient(135deg, var(--success-dark) 0%, var(--success-color) 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(56, 161, 105, 0.3);
    text-decoration: none;
    color: var(--text-inverse);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (min-width: 769px) {
    display: none;
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem 0.5rem;
    font-size: 0.8rem;
    gap: 0.375rem;
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    opacity: 0.8;
    transform: scale(1.02);
  }
  
  img {
    height: 40px;
    width: auto;
    
    @media (max-width: 768px) {
      height: 32px;
    }
  }
  
  h1 {
    color: var(--primary-color);
    font-family: 'Cormorant Garamond', serif;
    font-size: 1.5rem;
    font-weight: 500;
    letter-spacing: -0.025em;
    
    @media (max-width: 768px) {
      font-size: 1.25rem;
    }
  }
  
  span {
    color: var(--text-secondary);
    font-size: 0.875rem;
    font-family: 'Inter', sans-serif;
  }
`;


const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  justify-self: end;
  
  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

const NotificationButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0;
  transition: var(--transition);
  position: relative;
  
  &:hover {
    background-color: var(--background-secondary);
    color: var(--primary-color);
  }
`;

const NotificationBadge = styled.span`
  position: absolute;
  top: 0.25rem;
  right: 0.25rem;
  background-color: var(--secondary-color);
  color: var(--text-inverse);
  font-size: 0.75rem;
  padding: 0.125rem 0.375rem;
  border-radius: 0;
  min-width: 18px;
  text-align: center;
`;

const UserMenu = styled.div`
  position: relative;
`;

const UserButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0;
  transition: var(--transition);
  
  &:hover {
    background-color: var(--background-secondary);
  }
`;

const UserAvatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
`;

const FallbackAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: var(--contrast-accent);
  color: var(--text-inverse);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
`;

const UserInfo = styled.div`
  text-align: left;
  
  .name {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--text-primary);
  }
  
  .role {
    font-size: 0.75rem;
    color: var(--text-muted);
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const DropdownMenu = styled(motion.div)`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 0.5rem;
  background-color: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  box-shadow: var(--shadow-lg);
  min-width: 200px;
  z-index: 1000;
  overflow: hidden;
  
  @media (max-width: 768px) {
    right: -1rem;
    min-width: 180px;
  }
`;

const MenuItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: none;
  border: none;
  cursor: pointer;
  transition: var(--transition);
  color: var(--text-primary);
  font-size: 0.875rem;
  
  &:hover {
    background-color: var(--background-secondary);
  }
  
  &:first-child {
    border-bottom: 1px solid var(--border-color);
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0;
  transition: var(--transition);
  
  &:hover {
    background-color: var(--background-secondary);
    color: var(--text-primary);
  }
  
  @media (max-width: 768px) {
    display: block;
  }
`;

function Header({ onMenuToggle }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotificationPanel, setShowNotificationPanel] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);

  // Fetch notification count
  const fetchNotificationCount = async () => {
    try {
      const response = await apiService.getNotifications();
      if (response.success) {
        const unreadCount = response.notifications?.filter(n => !n.is_read).length || 0;
        setNotificationCount(unreadCount);
      }
    } catch (error) {
      logger.error('Error fetching notification count', { error });
      // For demo purposes, set a mock count
      setNotificationCount(3);
    }
  };

  useEffect(() => {
    if (user) {
      fetchNotificationCount();
      // Poll for new notifications every 30 seconds
      const interval = setInterval(fetchNotificationCount, 30000);
      return () => clearInterval(interval);
    } else {
      // Clear notification count when user is null
      setNotificationCount(0);
    }
  }, [user]);

  // Close panels when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showNotificationPanel || showUserMenu) {
        const target = event.target;
        const isNotificationPanel = target.closest('[data-notification-panel]');
        const isUserMenu = target.closest('[data-user-menu]');
        const isNotificationButton = target.closest('[data-notification-button]');
        const isUserButton = target.closest('[data-user-button]');
        
        if (!isNotificationPanel && !isNotificationButton && showNotificationPanel) {
          setShowNotificationPanel(false);
        }
        
        if (!isUserMenu && !isUserButton && showUserMenu) {
          setShowUserMenu(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showNotificationPanel, showUserMenu]);

  const handleLogout = async () => {
    setShowUserMenu(false);
    setShowNotificationPanel(false);
    // Set logged out flag BEFORE calling logout API to ensure it's set even if API fails
    localStorage.setItem('faithinsight_logged_out', 'true');
    await logout();
    navigate('/login');
  };

  const handleProfileClick = () => {
    navigate('/profile');
    setShowUserMenu(false);
  };

  const handleSettingsClick = () => {
    navigate('/settings');
    setShowUserMenu(false);
  };

  const handleNotificationClick = () => {
    setShowNotificationPanel(!showNotificationPanel);
    setShowUserMenu(false);
  };

  const handleNotificationClose = () => {
    setShowNotificationPanel(false);
  };

  const handleNotificationUpdate = () => {
    fetchNotificationCount();
  };

  const handleLogoClick = () => {
    // Navigate to dashboard based on user role
    if (user?.role === 'admin') {
      navigate('/admin/dashboard');
    } else if (user?.role === 'instructor') {
      navigate('/instructor/dashboard');
    } else if (user?.role === 'student') {
      navigate('/student/dashboard');
    } else {
      navigate('/dashboard');
    }
  };

  return (
    <>
      <HeaderContainer>
        <LeftSection>
          <MobileMenuButton onClick={onMenuToggle}>
            <FiMenu size={20} />
          </MobileMenuButton>
          
          <Logo onClick={handleLogoClick}>
            <img src={logo} alt="Faith Insight Logo" />
            <h1>Faith Insight</h1>
          </Logo>
        </LeftSection>

        <CenterSection>
          <DonateLink 
            to="/donation"
            title="Make a Donation"
          >
            <FiHeart size={16} />
            Donate & Support
          </DonateLink>
        </CenterSection>

        <RightSection>
        <NotificationButton 
          onClick={handleNotificationClick}
          data-notification-button
        >
          <FiBell size={20} />
          {notificationCount > 0 && (
            <NotificationBadge>{notificationCount}</NotificationBadge>
          )}
        </NotificationButton>

        <NotificationButton 
          onClick={toggleTheme} 
          title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
          style={{ 
            color: theme === 'dark' ? 'var(--primary-color)' : 'var(--text-secondary)',
            transition: 'color 0.3s ease'
          }}
        >
          {theme === 'light' ? <FiMoon size={20} /> : <FiSun size={20} />}
        </NotificationButton>

        <UserMenu data-user-menu>
          <UserButton 
            onClick={() => {
              setShowUserMenu(!showUserMenu);
              setShowNotificationPanel(false);
            }}
            data-user-button
          >
            {user?.avatar_url ? (
              <UserAvatar 
                src={user.avatar_url} 
                alt={`${user?.first_name} ${user?.last_name}`}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <FallbackAvatar style={{ display: user?.avatar_url ? 'none' : 'flex' }}>
              {(user?.first_name?.[0] || '') + (user?.last_name?.[0] || '')}
            </FallbackAvatar>
            <UserInfo>
              <div className="name">{user?.first_name} {user?.last_name}</div>
              <div className="role" style={{ 
                color: user?.role === 'admin' ? 'var(--primary-color)' : 'var(--text-muted)',
                fontWeight: user?.role === 'admin' ? '600' : '400'
              }}>
                {user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1)}
              </div>
            </UserInfo>
          </UserButton>

          <AnimatePresence>
            {showUserMenu && (
              <DropdownMenu
                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                transition={{ duration: 0.2 }}
              >
                <MenuItem onClick={handleProfileClick}>
                  <FiUser size={16} />
                  Profile
                </MenuItem>
                <MenuItem onClick={handleSettingsClick}>
                  <FiSettings size={16} />
                  Settings
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  <FiLogOut size={16} />
                  Sign Out
                </MenuItem>
              </DropdownMenu>
            )}
          </AnimatePresence>
        </UserMenu>

        {/* Notification Panel */}
        <NotificationPanel 
          isOpen={showNotificationPanel} 
          onClose={handleNotificationClose}
          onUpdate={handleNotificationUpdate}
        />
        </RightSection>
      </HeaderContainer>

      <MobileDonateSection>
        <MobileDonateLink 
          to="/donation"
          title="Make a Donation"
        >
          <FiHeart size={16} />
          Donate & Support
        </MobileDonateLink>
      </MobileDonateSection>
    </>
  );
}

export default Header;
